import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Outlet } from 'react-router-dom';

import Analytics from '../components/Template/Analytics';
import Navigation from '../components/Template/Navigation';
import SideBar from '../components/Template/SideBar';
import ScrollToTop from '../components/Template/ScrollToTop';
import ChatWidget from '../components/Template/ChatWidget';

const Main = (props) => {
  // Get current path, ensuring no double slashes
  const currentPath = typeof window !== 'undefined'
    ? window.location.pathname.replace(/^\/+/, '').replace(/\/+$/, '')
    : '';
  const canonicalUrl = currentPath
    ? `https://ajithjoseph.com/${currentPath}`
    : 'https://ajithjoseph.com';

  // Generate breadcrumb items based on current path
  const getBreadcrumbItems = () => {
    const items = [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://ajithjoseph.com',
      },
    ];

    if (currentPath) {
      const pathParts = currentPath.split('/').filter(Boolean);
      let accumulatedPath = '';

      pathParts.forEach((part, index) => {
        accumulatedPath += `/${part}`;
        const formattedName = part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, ' ');
        items.push({
          '@type': 'ListItem',
          position: index + 2,
          name: formattedName,
          item: `https://ajithjoseph.com${accumulatedPath}`,
        });
      });
    }

    return items;
  };

  return (
    <HelmetProvider>
      <Analytics />
      <ScrollToTop />
      <ChatWidget />
      <Helmet
        titleTemplate="%s | Ajith Joseph"
        defaultTitle="Ajith Joseph - AI Lead Engineer & Full Stack Developer"
        defer={false}
      >
        {props.title && <title>{props.title}</title>}
        <meta name="description" content={props.description} />
        <meta name="keywords" content="Ajith Joseph, AI Lead Engineer, Full Stack Developer, AI consultant Bengaluru, portfolio, React, .NET Core, Bengaluru, India, Healthcare Software, AI Automation, Vector Databases, RAG, retrieval augmented generation, n8n automation, WhatsApp bot, Chrome Extension AI, IBM Watsonx, Google Chrome AI Challenge, TabMaster AI, TextFlow Dev, PDF Chat Assistant, hire AI engineer India, freelance AI developer" />
        <meta name="author" content="Ajith Joseph" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href={canonicalUrl} />

        {/* OpenGraph for social sharing */}
        <meta property="og:title" content={props.title ? `${props.title} | Ajith Joseph` : 'Ajith Joseph - AI Lead Engineer & Full Stack Developer'} />
        <meta property="og:description" content={props.description} />
        <meta property="og:image" content="https://ajithjoseph.com/images/me.jpg" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="profile" />
        <meta property="og:site_name" content="Ajith Joseph - Portfolio" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={props.title ? `${props.title} | Ajith Joseph` : 'Ajith Joseph - AI Lead Engineer'} />
        <meta name="twitter:description" content={props.description} />
        <meta name="twitter:image" content="https://ajithjoseph.com/images/me.jpg" />

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Person',
                '@id': 'https://ajithjoseph.com/#person',
                name: 'Ajith Joseph',
                givenName: 'Ajith',
                familyName: 'Joseph',
                jobTitle: 'AI Lead Engineer',
                description: 'Bengaluru-based AI Lead Engineer with 13+ years in software engineering, including 3+ years building Agentic AI, RAG, and LLM systems using LangChain, n8n, and the Model Context Protocol, backed by a full-stack foundation in React, .NET Core, and Azure.',
                url: 'https://ajithjoseph.com',
                image: {
                  '@type': 'ImageObject',
                  url: 'https://ajithjoseph.com/images/me.jpg',
                  caption: 'Ajith Joseph - AI Lead Engineer & Full Stack Developer',
                },
                email: 'contact@ajithjoseph.com',
                telephone: '+919049222688',
                address: {
                  '@type': 'PostalAddress',
                  addressLocality: 'Bengaluru',
                  addressRegion: 'Karnataka',
                  postalCode: '560001',
                  addressCountry: 'IN',
                },
                sameAs: [
                  'https://www.linkedin.com/in/ajithjozef',
                  'https://github.com/codzz',
                  'https://blog.ajithjoseph.com',
                  'https://devpost.com/ajithjozef',
                  'https://www.softpedia.com/get/Internet/Download-Managers/Codzz-Youtube-Downloader.shtml',
                  'https://twitter.com/ajithjosef',
                  'https://www.facebook.com/ajithjozef',
                  'https://www.instagram.com/joseph__ajith/',
                  'https://angel.co/u/ajith-joseph-4',
                ],
                knowsAbout: [
                  'Agentic AI',
                  'Retrieval-Augmented Generation (RAG)',
                  'Large Language Models (LLMs)',
                  'LangChain',
                  'Model Context Protocol (MCP)',
                  'Prompt Engineering',
                  'Microsoft Copilot Studio',
                  'Microsoft Power Platform',
                  'Power Automate Custom Connectors',
                  'Microsoft Dataverse',
                  'Claude / Anthropic API',
                  'AI Engineering',
                  'Full Stack Development',
                  'Healthcare Software',
                  'React',
                  '.NET Core',
                  'Azure',
                  'Vector Databases',
                  'n8n Automation',
                  'AI Chatbots',
                  'Workflow Automation',
                  'Chrome Extensions',
                  'WhatsApp Bots',
                  'Telegram Bots',
                  'AI Image Generation',
                  'Next.js',
                  'Government Software',
                  'Hospital Management Systems',
                ],
                alumniOf: [
                  {
                    '@type': 'EducationalOrganization',
                    name: 'Sikkim Manipal University',
                  },
                  {
                    '@type': 'EducationalOrganization',
                    name: 'Bharathidasan University',
                  },
                  {
                    '@type': 'EducationalOrganization',
                    name: 'Kannur University',
                  },
                ],
                award: [
                  'IBM Watsonx Hackathon Top 100 Scorer',
                  'Google Chrome Built-in AI Challenge 2024 Participant (TabMaster AI)',
                  'Google Chrome Built-in AI Challenge 2024 Participant (TextFlow Dev)',
                  'Google Chrome Built-in AI Challenge 2024 Participant (PDF Chat Assistant)',
                ],
                hasOccupation: {
                  '@type': 'Occupation',
                  name: 'AI Lead Engineer',
                  occupationLocation: {
                    '@type': 'City',
                    name: 'Bengaluru',
                  },
                },
                contactPoint: {
                  '@type': 'ContactPoint',
                  contactType: 'professional',
                  email: 'contact@ajithjoseph.com',
                  telephone: '+919049222688',
                  availableLanguage: ['English', 'Malayalam', 'Hindi'],
                  areaServed: 'Worldwide',
                },
                nationality: {
                  '@type': 'Country',
                  name: 'India',
                },
                worksFor: {
                  '@type': 'Organization',
                  name: 'Confidential',
                  description: 'AI Lead Engineer role leading an AI engineering team focused on Agentic AI, RAG systems, and enterprise workflow automation',
                },
              },
              {
                '@type': 'ProfessionalService',
                '@id': 'https://ajithjoseph.com/#service',
                name: 'Ajith Joseph - AI & Full Stack Development Services',
                description: 'Expert Agentic AI, RAG, and full-stack development services specializing in LangChain, n8n, React, .NET Core, AI chatbots, workflow automation, and healthcare software.',
                provider: {
                  '@id': 'https://ajithjoseph.com/#person',
                },
                areaServed: [
                  {
                    '@type': 'City',
                    name: 'Bengaluru',
                  },
                  {
                    '@type': 'Country',
                    name: 'India',
                  },
                  'Worldwide (Remote)',
                ],
                serviceType: [
                  'Agentic AI Development',
                  'Retrieval-Augmented Generation (RAG)',
                  'AI Engineering',
                  'Full Stack Development',
                  'Healthcare Software Development',
                  'Workflow Automation',
                  'Chrome Extension Development',
                  'AI Chatbot Development',
                ],
                url: 'https://ajithjoseph.com',
                telephone: '+919049222688',
                email: 'contact@ajithjoseph.com',
                priceRange: '$$',
              },
              {
                '@type': 'WebSite',
                '@id': 'https://ajithjoseph.com/#website',
                url: 'https://ajithjoseph.com',
                name: 'Ajith Joseph | AI Lead Engineer & Full Stack Developer',
                publisher: {
                  '@id': 'https://ajithjoseph.com/#person',
                },
                potentialAction: {
                  '@type': 'SearchAction',
                  target: 'https://ajithjoseph.com/?s={search_term_string}',
                  'query-input': 'required name=search_term_string',
                },
              },
              {
                '@type': ['WebPage', 'ProfilePage'],
                '@id': `${canonicalUrl}#webpage`,
                url: canonicalUrl,
                name: props.title || 'Ajith Joseph - AI Lead Engineer & Full Stack Developer',
                headline: props.title ? `${props.title} | Ajith Joseph` : 'Ajith Joseph - AI Lead Engineer & Full Stack Developer',
                description: props.description || 'AI Lead Engineer with 13+ years experience specializing in AI Automation, Healthcare Software, and Enterprise Architecture.',
                inLanguage: 'en-US',
                isPartOf: {
                  '@id': 'https://ajithjoseph.com/#website',
                },
                about: {
                  '@id': 'https://ajithjoseph.com/#person',
                },
                mainEntity: {
                  '@id': 'https://ajithjoseph.com/#person',
                },
                dateModified: '2026-07-11',
                datePublished: '2024-01-01',
                speakable: {
                  '@type': 'SpeakableSpecification',
                  cssSelector: ['#intro', '#expertise', '#faq', '.faq-item', 'article.post > p:first-of-type'],
                },
              },
              {
                '@type': 'BreadcrumbList',
                '@id': `${canonicalUrl}#breadcrumb`,
                itemListElement: getBreadcrumbItems(),
              },
            ],
          })}
        </script>
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-5K2Q8M47EY" />
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', 'G-5K2Q8M47EY'); 
          `}
        </script>
        <script type="text/javascript">
          {`
            (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/576tpvydaf";
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script");
          `}
        </script>
      </Helmet>
      <div id="wrapper">
        <Navigation />
        <div id="main">
          <Suspense fallback={<div />}>
            <Outlet />
          </Suspense>
        </div>
        {props.fullPage ? null : <SideBar />}
      </div>
    </HelmetProvider>
  );
};

Main.propTypes = {
  fullPage: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string,
};

Main.defaultProps = {
  fullPage: false,
  title: null,
  description: 'Bengaluru-based AI Lead Engineer with 13+ years in software engineering, 3+ years in Agentic AI, RAG & LLM systems. Google Chrome AI Challenge Participant & IBM Watsonx Top 100.',
};

export default Main;
